type MeticulousValue = string | boolean;

export type MeticulousFlagOverride =
  { overridden: false } | { overridden: true; value: MeticulousValue };

interface MeticulousContextApi {
  recordUserId(userId: string): void;
  recordUserEmail(email: string): void;
  recordCustomContext(label: string, value: MeticulousValue): void;
  recordFeatureFlag(label: string, value: MeticulousValue): void;
  getFlagOverride(label: string): MeticulousFlagOverride;
}

declare global {
  interface Window {
    Meticulous?: {
      isRunningAsTest?: boolean;
      context?: MeticulousContextApi;
    };
  }
}

export const isRunningAsMeticulousTest =
  window.Meticulous?.isRunningAsTest === true;

const meticulousContext = () => window.Meticulous?.context;

export const isMeticulousRecorderActive = () =>
  meticulousContext() !== undefined;

export const recordMeticulousUser = (user: {
  uid: string;
  email: string | null;
}) => {
  const context = meticulousContext();
  if (!context) return;
  context.recordUserId(user.uid);
  if (user.email) context.recordUserEmail(user.email);
};

export const recordMeticulousCustomContext = (
  label: string,
  value: MeticulousValue,
) => meticulousContext()?.recordCustomContext(label, value);

export const recordMeticulousFeatureFlags = (
  flags: Record<string, MeticulousValue>,
) => {
  const context = meticulousContext();
  if (!context) return;
  Object.entries(flags).forEach(([label, value]) =>
    context.recordFeatureFlag(label, value),
  );
};

export const withMeticulousFlagOverrides = <
  T extends Record<string, MeticulousValue>,
>(
  flags: T,
): T => {
  const context = meticulousContext();
  if (!context) return flags;
  const resolved = Object.entries(flags).map(([label, value]) => {
    const override = context.getFlagOverride(label);
    return [label, override.overridden ? override.value : value];
  });
  return Object.fromEntries(resolved) as T;
};
